import React, { useEffect, useState } from 'react'
import moment from 'moment'
import toast from 'react-hot-toast'
import Spinwheel from '../../../components/common/Spinwheel'
import {
  generateDlrReportApi,
  getDlrReportDetailsApi,
  getGeneratedDlrReportFileApi,
  getGeneratedDlrReportsApi,
  shareGeneratedDlrReportApi,
  unshareGeneratedDlrReportApi,
} from '../../../utils/ApiEndpointFunctions'
import { useUserContext } from '../../../context/userContext'
import './DLRReport.css'

const PAGE_LIMIT = 10
const personName = person => [person?.firstName, person?.lastName].filter(Boolean).join(' ') || '-'

function DLRReport() {
  const { langPreference, userDetails } = useUserContext()
  const normalizedRole = String(userDetails?.roleId?.type || userDetails?.roleId?.name || '').toLowerCase().replace(/[^a-z]/g, '')
  const isInspector = normalizedRole === 'inspector'
  const canUnshare = normalizedRole === 'owner' || normalizedRole === 'admin'
  const [screen, setScreen] = useState('list')
  const [range, setRange] = useState({ from: '', to: '' })
  const [listFilters, setListFilters] = useState({ from: '', to: '' })
  const [appliedFilters, setAppliedFilters] = useState({ from: '', to: '' })
  const [report, setReport] = useState(null)
  const [reports, setReports] = useState([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)
  const [listLoading, setListLoading] = useState(true)
  const [actionId, setActionId] = useState('')

  useEffect(() => {
    let active = true
    const loadGeneratedReports = async () => {
      try {
        setListLoading(true)
        const response = await getGeneratedDlrReportsApi({ ...appliedFilters, page, limit: PAGE_LIMIT })
        if (!active) return
        setReports(response?.data?.data?.reports || [])
        setTotalPages(Math.max(response?.data?.data?.totalPages || 1, 1))
      } catch (error) {
        if (!active) return
        setReports([])
        toast.error(error?.response?.data?.message || 'Unable to load generated reports.')
      } finally {
        if (active) setListLoading(false)
      }
    }
    loadGeneratedReports()
    return () => { active = false }
  }, [appliedFilters, page])

  const validateRange = values => {
    if (!values.from || !values.to) {
      toast.error(langPreference?.select_date_range_dlr || 'Select both from and to dates.')
      return false
    }
    if (values.from > values.to) {
      toast.error(langPreference?.invalid_date_range || 'From date cannot be after to date.')
      return false
    }
    return true
  }

  const loadPreview = async () => {
    if (!validateRange(range)) return
    try {
      setLoading(true)
      const response = await getDlrReportDetailsApi(range.from, range.to)
      setReport(response?.data?.data || null)
      setScreen('preview')
    } catch (error) {
      setReport(null)
      toast.error(error?.response?.data?.message || 'Unable to load the DLR report.')
    } finally {
      setLoading(false)
    }
  }

  const fetchStoredFile = async (storedReport, disposition, previewWindow = null) => {
    const response = await getGeneratedDlrReportFileApi(storedReport._id, disposition)
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
    if (disposition === 'inline') {
      if (previewWindow) previewWindow.location.href = url
      else window.open(url, '_blank', 'noopener,noreferrer')
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000)
      return
    }
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', storedReport.fileName || `DLR_Report_${moment(storedReport.fromDate).format('YYYY-MM-DD')}_to_${moment(storedReport.toDate).format('YYYY-MM-DD')}.pdf`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const savePreview = async () => {
    try {
      setActionId('preview-save')
      await generateDlrReportApi({ ...range, sendToInspection: false })
      toast.success(langPreference?.report_saved || 'Report saved successfully')
      setReport(null)
      setScreen('list')
      setAppliedFilters(current => ({ ...current }))
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to save the report.')
    } finally {
      setActionId('')
    }
  }

  const handleStoredFile = async (storedReport, disposition) => {
    const previewWindow = disposition === 'inline' ? window.open('', '_blank') : null
    if (previewWindow) previewWindow.opener = null
    try {
      setActionId(`${storedReport._id}-${disposition}`)
      await fetchStoredFile(storedReport, disposition, previewWindow)
    } catch (error) {
      if (previewWindow) previewWindow.close()
      toast.error(error?.response?.data?.message || 'Unable to open the report.')
    } finally {
      setActionId('')
    }
  }

  const shareStoredReport = async storedReport => {
    try {
      setActionId(`${storedReport._id}-share`)
      const response = await shareGeneratedDlrReportApi(storedReport._id)
      setReports(current => current.map(item => item._id === storedReport._id ? response?.data?.data : item))
      toast.success(langPreference?.report_sent_to_inspection || 'Report sent to inspection')
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to send the report to inspection.')
    } finally {
      setActionId('')
    }
  }

  const unshareStoredReport = async storedReport => {
    try {
      setActionId(`${storedReport._id}-unshare`)
      const response = await unshareGeneratedDlrReportApi(storedReport._id)
      setReports(current => current.map(item => item._id === storedReport._id ? response?.data?.data : item))
      toast.success(langPreference?.report_unshared || 'Report removed from inspection')
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to unshare the report.')
    } finally {
      setActionId('')
    }
  }

  const applyListFilters = () => {
    if (listFilters.from && listFilters.to && !validateRange(listFilters)) return
    setPage(1)
    setAppliedFilters(listFilters)
  }

  const resetListFilters = () => {
    setListFilters({ from: '', to: '' })
    setAppliedFilters({ from: '', to: '' })
    setPage(1)
  }

  const startGeneration = () => {
    setRange({ from: '', to: '' })
    setReport(null)
    setScreen('range')
  }

  const updateReportRange = (field, value) => {
    setRange(current => ({ ...current, [field]: value }))
    if (screen === 'preview') setReport(null)
  }

  const returnToList = () => {
    setScreen('list')
    setAppliedFilters(current => ({ ...current }))
  }

  const renderReportDocument = () => (
    <div className="periodic-dlr-document">
      <div className="periodic-dlr-document__title">
        <span>{report.meta?.companyName}</span>
        <h4>{langPreference?.daily_labour_report || 'Daily Labour Report'}</h4>
      </div>
      <div className="periodic-dlr-meta">
        <div><span>{langPreference?.contractor_name || 'Name of Contractor'}</span><strong>{report.meta?.companyName || '-'}</strong></div>
        <div><span>{langPreference?.name_of_work || 'Name of Work'}</span><strong>{report.meta?.workName || report.meta?.siteName || '-'}</strong></div>
        <div><span>{langPreference?.agreement_number || 'Agreement/Bond No.'}</span><strong>{report.meta?.agreementNumber || '-'}</strong></div>
        <div><span>{langPreference?.agreement_date || 'Agreement Date'}</span><strong>{report.meta?.agreementDate ? moment(report.meta.agreementDate).format('DD/MM/YYYY') : '-'}</strong></div>
        <div className="periodic-dlr-meta__period"><span>{langPreference?.period || 'Period'}</span><strong>{moment(report.meta?.from).format('DD/MM/YYYY')} - {moment(report.meta?.to).format('DD/MM/YYYY')}</strong></div>
      </div>
      <div className="table-responsive periodic-dlr-table-wrap">
        <table className="table periodic-dlr-table">
          <thead><tr>
            <th>{langPreference?.serial_no || 'S.No.'}</th><th>{langPreference?.date || 'Date'}</th><th>{langPreference?.work_item || 'Work Item'}</th><th>{langPreference?.location || 'Location'}</th>
            <th className="text-end">{langPreference?.tech || 'Tech'}</th><th className="text-end">{langPreference?.non_tech || 'Non-Tech'}</th><th className="text-end">{langPreference?.total || 'Total'}</th><th>{langPreference?.remarks || 'Remarks'}</th>
          </tr></thead>
          <tbody>
            {(() => {
              let serial = 0
              return report.dates.flatMap(dateGroup => {
                const dateRows = dateGroup.workItems.reduce((sum, work) => sum + Math.max(work.locations.length, 1), 0)
                let datePrinted = false
                return dateGroup.workItems.flatMap(work => {
                  const locations = work.locations.length ? work.locations : [{ location: '', technical: 0, nonTechnical: 0, total: 0, remarks: '' }]
                  return locations.map((location, locationIndex) => {
                    serial += 1
                    const row = <tr key={`${dateGroup.date}-${work.workItem}-${location.location}-${locationIndex}`}>
                      <td className="periodic-dlr-table__serial">{serial}</td>
                      {!datePrinted && <td rowSpan={dateRows} className="periodic-dlr-table__date">{moment(dateGroup.date).format('DD/MM/YYYY')}</td>}
                      {locationIndex === 0 && <td rowSpan={locations.length} className="periodic-dlr-table__work">{work.workItem || '-'}</td>}
                      <td>{location.location || '-'}</td><td className="text-end">{location.technical}</td><td className="text-end">{location.nonTechnical}</td><td className="text-end">{location.total}</td><td>{location.remarks || ''}</td>
                    </tr>
                    datePrinted = true
                    return row
                  })
                })
              })
            })()}
          </tbody>
          <tfoot><tr><td colSpan="4">{langPreference?.total_count || 'Total Count'}</td><td className="text-end">{report.totals?.technical || 0}</td><td className="text-end">{report.totals?.nonTechnical || 0}</td><td className="text-end">{report.totals?.total || 0}</td><td></td></tr></tfoot>
        </table>
      </div>
      <div className="periodic-dlr-submission">
        {langPreference?.submitted_by || 'Submitted by'} M/S {report.meta?.companyName}{report.meta?.generatedBy ? `, ${report.meta.generatedBy}` : ''}
        <small>{langPreference?.computer_generated_report || 'This is a computer-generated report and does not require a signature.'}</small>
      </div>
    </div>
  )

  return (
    <div className="main-content periodic-dlr-page">
      <div className="container-fluid">
        <div className="periodic-dlr-header">
          <div>
            <h3 className="mb-1 font-blue">{screen === 'list' ? (langPreference?.dlr_report_listing || 'DLR Report Listing') : (langPreference?.periodic_dlr_report || 'DLR Report')}</h3>
            <p>{screen === 'list'
              ? (isInspector
                  ? (langPreference?.inspector_reports_description || 'This page lists DLR reports shared for inspection. Use the date filter to find a report, then view or download it.')
                  : (langPreference?.generated_reports_description || 'View and manage generated DLR reports for this site. Click Generate Report to select a reporting period and create a new report. Saved reports can be viewed, downloaded or sent for inspection.'))
              : (langPreference?.periodic_dlr_description || 'Consolidated labour counts grouped by date, work item and location.')}</p>
          </div>
          <div className="periodic-dlr-header__actions">
            {screen !== 'list' && <button type="button" className="btn filter" onClick={returnToList}>{langPreference?.back_to_reports || 'Back to Reports'}</button>}
            {screen === 'list' && !isInspector && <button type="button" className="btn create-order" onClick={startGeneration}>{langPreference?.generate_report || 'Generate Report'}</button>}
            {screen === 'preview' && report?.dates?.length > 0 && !isInspector && <button type="button" className="btn create-order" onClick={savePreview} disabled={Boolean(actionId)}>{actionId === 'preview-save' ? (langPreference?.loading || 'Loading...') : (langPreference?.save_report || 'Save Report')}</button>}
          </div>
        </div>

        {screen === 'list' && <>
          <div className="periodic-dlr-filters periodic-dlr-filters--list">
            <div><label htmlFor="generated-report-from">{langPreference?.from_date || 'From Date'}</label><input id="generated-report-from" type="date" className="form-control" value={listFilters.from} max={listFilters.to || undefined} onChange={event => setListFilters(current => ({ ...current, from: event.target.value }))} /></div>
            <div><label htmlFor="generated-report-to">{langPreference?.to_date || 'To Date'}</label><input id="generated-report-to" type="date" className="form-control" value={listFilters.to} min={listFilters.from || undefined} onChange={event => setListFilters(current => ({ ...current, to: event.target.value }))} /></div>
            <button type="button" className="btn btn-dark" onClick={applyListFilters}>{langPreference?.apply || 'Apply'}</button>
            <button type="button" className="btn filter" onClick={resetListFilters}>{langPreference?.reset_filter || 'Reset Filter'}</button>
          </div>
          {listLoading ? <div className="periodic-dlr-state"><Spinwheel /></div> : reports.length === 0 ? (
            <div className="periodic-dlr-empty"><strong>{langPreference?.no_generated_reports || 'No reports generated yet'}</strong><span>{isInspector ? (langPreference?.no_shared_reports || 'Reports sent to inspection will appear here.') : (langPreference?.generate_first_report || 'Generate a report to create the first saved PDF.')}</span></div>
          ) : <>
            <div className="table-responsive periodic-dlr-history">
              <table className="table align-middle"><thead><tr><th>{langPreference?.report_period || 'Report Period'}</th><th>{langPreference?.generated_on || 'Generated On'}</th><th>{langPreference?.generated_by || 'Generated By'}</th><th>{langPreference?.sharing_status || 'Sharing Status'}</th><th>{langPreference?.shared_on || 'Shared On'}</th><th>{langPreference?.action || 'Action'}</th></tr></thead>
                <tbody>{reports.map(item => <tr key={item._id}>
                  <td><strong>{moment(item.fromDate).format('DD/MM/YYYY')}</strong><span className="periodic-dlr-history__range">to <strong>{moment(item.toDate).format('DD/MM/YYYY')}</strong></span></td>
                  <td>{moment(item.generatedOn).format('DD/MM/YYYY, hh:mm a')}</td><td>{personName(item.generatedBy)}</td>
                  <td><span className={`periodic-dlr-status periodic-dlr-status--${item.status}`}>{item.status === 'shared' ? (langPreference?.shared || 'Shared') : (langPreference?.not_shared || 'Not Shared')}</span></td>
                  <td>{item.sharedOn ? moment(item.sharedOn).format('DD/MM/YYYY, hh:mm a') : '-'}</td>
                  <td><div className="periodic-dlr-actions">
                    <button type="button" className="btn btn-sm filter" disabled={Boolean(actionId)} onClick={() => handleStoredFile(item, 'inline')}>{langPreference?.view || 'View'}</button>
                    <button type="button" className="btn btn-sm filter" disabled={Boolean(actionId)} onClick={() => handleStoredFile(item, 'attachment')}>{langPreference?.download || 'Download'}</button>
                    {!isInspector && item.status !== 'shared' && <button type="button" className="btn btn-sm create-order" disabled={Boolean(actionId)} onClick={() => shareStoredReport(item)}>{actionId === `${item._id}-share` ? (langPreference?.sending || 'Sending...') : (langPreference?.send_to_inspection || 'Send to Inspection')}</button>}
                    {canUnshare && item.status === 'shared' && <button type="button" className="btn btn-sm btn-dark" disabled={Boolean(actionId)} onClick={() => unshareStoredReport(item)}>{actionId === `${item._id}-unshare` ? (langPreference?.loading || 'Loading...') : (langPreference?.unshare || 'Unshare')}</button>}
                  </div></td>
                </tr>)}</tbody>
              </table>
            </div>
            {totalPages > 1 && <div className="periodic-dlr-pagination"><button className="btn filter" disabled={page === 1} onClick={() => setPage(current => current - 1)}>{langPreference?.previous_page || 'Previous'}</button><span>{page} / {totalPages}</span><button className="btn filter" disabled={page === totalPages} onClick={() => setPage(current => current + 1)}>{langPreference?.next_page || 'Next'}</button></div>}
          </>}
        </>}

        {(screen === 'range' || screen === 'preview') && <div className="periodic-dlr-generator">
          <h5>{langPreference?.select_report_period || 'Select Report Period'}</h5>
          <p>{langPreference?.generate_report_help || 'Select the From and To dates, click Apply to review the consolidated DLR, then click Save Report.'}</p>
          <div className="periodic-dlr-filters">
            <div><label htmlFor="dlr-report-from">{langPreference?.from_date || 'From Date'}</label><input id="dlr-report-from" type="date" className="form-control" value={range.from} max={range.to || undefined} onChange={event => updateReportRange('from', event.target.value)} /></div>
            <div><label htmlFor="dlr-report-to">{langPreference?.to_date || 'To Date'}</label><input id="dlr-report-to" type="date" className="form-control" value={range.to} min={range.from || undefined} onChange={event => updateReportRange('to', event.target.value)} /></div>
            <button type="button" className="btn btn-dark" onClick={loadPreview} disabled={loading}>{loading ? (langPreference?.loading || 'Loading...') : (langPreference?.apply || 'Apply')}</button>
          </div>
        </div>}

        {screen === 'preview' && (loading ? <div className="periodic-dlr-state"><Spinwheel /></div> : !report ? <div className="periodic-dlr-state">{langPreference?.select_date_range_dlr || 'Select a date range to view the DLR report.'}</div> : report.dates?.length === 0 ? <div className="periodic-dlr-state">{langPreference?.data_not_found || 'Data not found'}</div> : renderReportDocument())}
      </div>
    </div>
  )
}

export default DLRReport
